import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import ImageOptimizer from "../components/ImageOptimizer";

// Importar las imágenes originales como fallback
import JorgeFotoOriginal from "../images/personas/Jorge.png";
import TristanFotoOriginal from "../images/personas/Tristan.png";
import GuadalupeFotoOriginal from "../images/personas/Guadalupe.png";
import IrinaFotoOriginal from "../images/personas/Irina.png";
import FrancinaFotoOriginal from "../images/personas/Francina.png";
import ElisaFotoOriginal from "../images/personas/Elisa.png";
import TodosFotoOriginal from "../images/personas/Todos.png";
import AboutFondoOriginal from "../images/Fondos/AboutFondo.jpg";

// URLs de las imágenes WebP optimizadas
const JorgeWebP = "/images/personas/Jorge.webp";
const TristanWebP = "/images/personas/Tristan.webp";
const GuadalupeWebP = "/images/personas/Guadalupe.webp";
const IrinaWebP = "/images/personas/Irina.webp";
const FrancinaWebP = "/images/personas/Francina.webp";
const ElisaWebP = "/images/personas/Elisa.webp";
const TodosWebP = "/images/personas/Todos.webp";
const AboutFondoWebP = "/images/Fondos/AboutFondo.webp";

const AboutSection = () => {
  const [supportsWebP, setSupportsWebP] = useState(true);
  
  // Detectar soporte de WebP
  useEffect(() => {
    const checkWebPSupport = async () => {
      try {
        const img = new Image();
        img.onload = () => setSupportsWebP(img.width > 0 && img.height > 0);
        img.onerror = () => setSupportsWebP(false);
        img.src = 'data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==';
      } catch (err) {
        setSupportsWebP(false);
      }
    };
    
    checkWebPSupport();
  }, []);
  
  // Obtener URLs de imágenes según soporte del navegador
  const getImageSrc = (webp, original) => {
    return supportsWebP ? webp : original;
  };

  const teamSliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    fade: false,
    arrows: false,
    lazyLoad: 'progressive',
  };

  // Datos del equipo con imágenes optimizadas
  const teamMembers = [
    {
      name: "Jorge Echegaray",
      role: "Socio Gerente",
      imageWebP: JorgeWebP,
      imageOriginal: JorgeFotoOriginal,
    },
    {
      name: "Tristán Echegaray",
      role: "Responsable Logístico",
      imageWebP: TristanWebP,
      imageOriginal: TristanFotoOriginal,
    },
    {
      name: "Guadalupe Sueldo",
      role: "Coordinadora Logística",
      imageWebP: GuadalupeWebP,
      imageOriginal: GuadalupeFotoOriginal,
    },
    {
      name: "Irina Antonutti",
      role: "Contadora",
      imageWebP: IrinaWebP,
      imageOriginal: IrinaFotoOriginal,
    },
    {
      name: "Francina Coseani",
      role: "Analista Administrativa",
      imageWebP: FrancinaWebP,
      imageOriginal: FrancinaFotoOriginal,
    },
    {
      name: "Elisa Fernández Echegaray",
      role: "Departamento Comercial",
      imageWebP: ElisaWebP,
      imageOriginal: ElisaFotoOriginal,
    },
    {
      name: "Nosotros",
      role: "",
      imageWebP: TodosWebP,
      imageOriginal: TodosFotoOriginal,
      noRole: true,
    }
  ];

  return (
    <section
      id="About"
      className="relative w-full mt-0 py-12 md:py-24 lg:py-32 lg:mt-56 text-white overflow-hidden"
    >
      {/* Fondo con imagen y gradiente */}
      <div className="absolute inset-0 z-0">
        <ImageOptimizer
          src={getImageSrc(AboutFondoWebP, AboutFondoOriginal)}
          alt="Fondo Montaña"
          objectFit="cover"
          priority={true}
        />
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(90deg, rgba(20, 24, 54, 0.95) 0%, rgba(20, 24, 54, 0.2) 42%, rgba(20, 24, 54, 0) 49%, rgba(20, 24, 54, 0) 51%, rgba(20, 24, 54, 0.2) 58%, rgba(20, 24, 54, 0.95) 100%)
            `
          }}
        />
      </div>

      {/* Contenido */}
      <div className="container relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 px-4 md:px-6">
        <div className="space-y-6 flex flex-col justify-center items-start max-w-2xl">
          <h2 className="text-2xl font-bold tracking-tight">
            ¿QUÉ HACEMOS Y QUIÉNES SOMOS?
          </h2>
          <div className="space-y-6 text-base">
            <p className="leading-relaxed">
              Somos una empresa de la provincia de Córdoba, fundada por Jorge Echegaray Pinto, en el año 2015. Nos especializamos en la gestión y coordinación de cargas generales, peligrosas y a granel, siendo intermediarios entre el cliente y el transporte.
            </p>
            <p className="leading-relaxed">
              Transportamos mercadería en toda la región Argentina y contamos con más de 2500 unidades de camiones de terceros; de corta y larga distancia. Somos un equipo organizado que trabaja para brindar un servicio de transporte integro con miras al crecimiento de nuestra empresa, nuestros empleados y nuestros proveedores.
            </p>
          </div>
        </div>

        <div className="flex flex-col items-end">
          <h2 className="text-2xl font-bold mb-6">EL EQUIPO</h2>
          <div className="w-full max-w-lg">
            <Slider {...teamSliderSettings}>
              {teamMembers.map((member, index) => (
                <div className="relative pb-4" key={index}>
                  <div className="w-full h-[400px] overflow-hidden rounded-lg">
                    <ImageOptimizer
                      src={getImageSrc(member.imageWebP, member.imageOriginal)}
                      alt={member.name}
                      objectFit="contain"
                      className="hover:scale-105 transition-transform duration-300"
                      priority={index === 0}
                    />
                  </div>
                  <div 
                    className="absolute bottom-0 left-0 right-0 p-2 text-center flex justify-center items-center gap-3 px-6"
                    style={{
                      background: 'rgba(20, 24, 54, 0.95)'
                    }}
                  >
                    <h3 className="text-xl font-semibold">{member.name}</h3>
                    {!member.noRole && (
                      <>
                        <span className="text-white/80">-</span>
                        <p className="text-base text-white/80">{member.role}</p>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
